import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FaultDiagnosisFlow = () => {
  const [currentStep, setCurrentStep] = useState('start');
  const [history, setHistory] = useState(['start']);

  const flowSteps = {
    start: {
      question: "What electrical problem are you experiencing?",
      icon: "HelpCircle",
      color: "var(--color-primary)",
      options: [
        { id: "no-power", label: "No Power / Complete Outage", next: "no-power" },
        { id: "partial", label: "Partial Power Loss", next: "partial" },
        { id: "sparks", label: "Sparks or Burning Smell", next: "emergency" },
        { id: "flickering", label: "Flickering Lights", next: "flickering" }
      ]
    },
    "no-power": {
      question: "Is the entire property without power?",
      icon: "Power",
      color: "var(--color-error)",
      options: [
        { id: "entire", label: "Yes, entire property", next: "check-main" },
        { id: "section", label: "No, only certain areas", next: "check-breaker" }
      ]
    },
    "check-main": {
      question: "Have you checked your main circuit breaker?",
      icon: "ToggleLeft",
      color: "var(--color-warning)",
      options: [
        { id: "tripped", label: "It\'s tripped/off", next: "reset-main" },
        { id: "on", label: "It\'s on but no power", next: "call-emergency" }
      ]
    },
    "reset-main": {
      question: "After resetting, does the breaker trip again immediately?",
      icon: "RotateCcw",
      color: "var(--color-warning)",
      options: [
        { id: "yes-trip", label: "Yes, trips immediately", next: "call-emergency" },
        { id: "stays", label: "No, power restored", next: "monitor" }
      ]
    },
    "check-breaker": {
      question: "Can you identify which circuit breaker is tripped?",
      icon: "Search",
      color: "var(--color-accent)",
      options: [
        { id: "found", label: "Yes, found the tripped breaker", next: "reset-breaker" },
        { id: "not-found", label: "No tripped breakers visible", next: "call-professional" }
      ]
    },
    "reset-breaker": {
      question: "After resetting the breaker, what happens?",
      icon: "ToggleRight",
      color: "var(--color-success)",
      options: [
        { id: "fixed", label: "Power restored, working fine", next: "monitor" },
        { id: "trips-again", label: "Trips again immediately", next: "call-professional" }
      ]
    },
    partial: {
      question: "Which areas are affected?",
      icon: "Home",
      color: "var(--color-accent)",
      options: [
        { id: "kitchen", label: "Kitchen appliances", next: "check-breaker" },
        { id: "lights", label: "Lighting circuits", next: "flickering" },
        { id: "outlets", label: "Specific outlets", next: "check-breaker" }
      ]
    },
    flickering: {
      question: "When do the lights flicker?",
      icon: "Lightbulb",
      color: "var(--color-warning)",
      options: [
        { id: "appliance", label: "When using specific appliances", next: "overload" },
        { id: "random", label: "Randomly throughout the day", next: "call-professional" },
        { id: "weather", label: "During storms/wind", next: "external-check" }
      ]
    },
    overload: {
      question: "Are you using multiple high-power appliances simultaneously?",
      icon: "Zap",
      color: "var(--color-warning)",
      options: [
        { id: "yes-overload", label: "Yes, multiple appliances", next: "reduce-load" },
        { id: "no-overload", label: "No, single appliance causes it", next: "call-professional" }
      ]
    },
    "external-check": {
      question: "Have you checked for external issues (power lines, weather box)?",
      icon: "CloudLightning",
      color: "var(--color-secondary)",
      options: [
        { id: "external-issue", label: "Visible external damage", next: "call-utility" },
        { id: "no-external", label: "No external issues visible", next: "call-professional" }
      ]
    },
    emergency: {
      result: "IMMEDIATE ACTION REQUIRED",
      icon: "AlertTriangle",
      color: "var(--color-error)",
      message: "Sparks or burning smells indicate a serious electrical hazard. Turn off power at the main breaker if safe to do so and evacuate the area.",
      action: "Call Emergency: 082 793 5785",
      urgent: true
    },
    "call-emergency": {
      result: "Professional Help Needed Urgently",
      icon: "Phone",
      color: "var(--color-error)",
      message: "This indicates a serious electrical fault that requires immediate professional attention. Do not attempt further troubleshooting.",
      action: "Call Now: 082 793 5785",
      urgent: true
    },
    "call-professional": {
      result: "Schedule Professional Inspection",
      icon: "UserCheck",
      color: "var(--color-accent)",
      message: "This issue requires professional diagnosis and repair. While not immediately dangerous, it should be addressed promptly to prevent further problems.",
      action: "Book Appointment: 082 793 5785",
      urgent: false
    },
    monitor: {
      result: "Monitor the Situation",
      icon: "Eye",
      color: "var(--color-success)",
      message: "Power has been restored. Monitor the circuit for the next 24-48 hours. If the problem recurs, contact a professional for inspection.",
      action: "Save Contact: 082 793 5785",
      urgent: false
    },
    "reduce-load": {
      result: "Circuit Overload Detected",
      icon: "TrendingDown",
      color: "var(--color-warning)",
      message: "Your circuit is overloaded. Distribute appliances across different circuits. If problem persists, you may need additional circuits installed.",
      action: "Consult Expert: 082 793 5785",
      urgent: false
    },
    "call-utility": {
      result: "Contact Your Utility Provider",
      icon: "Building2",
      color: "var(--color-secondary)",
      message: "The issue appears to be with external power supply. Contact your utility provider first. If they confirm supply is normal, call us for internal inspection.",
      action: "Backup Contact: 082 793 5785",
      urgent: false
    }
  };

  const handleOptionClick = (nextStep) => {
    setHistory([...history, nextStep]);
    setCurrentStep(nextStep);
  };

  const handleBack = () => {
    if (history?.length > 1) {
      const newHistory = [...history];
      newHistory?.pop();
      setHistory(newHistory);
      setCurrentStep(newHistory?.[newHistory?.length - 1]);
    }
  };

  const handleReset = () => {
    setCurrentStep('start');
    setHistory(['start']);
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:0827935785';
  };

  const step = flowSteps?.[currentStep];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-4">
            Interactive Fault Diagnosis
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground">
            Answer a few questions to understand your electrical issue and get immediate guidance on next steps.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8 lg:p-10 shadow-elevated">
          <div className="flex items-center justify-center mb-6 md:mb-8">
            <div
              className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full"
              style={{ backgroundColor: `${step?.color}15` }}
            >
              <Icon name={step?.icon} size={32} color={step?.color} />
            </div>
          </div>

          {step?.question ? (
            <>
              <h3 className="font-headline font-semibold text-xl md:text-2xl text-foreground text-center mb-6 md:mb-8">
                {step?.question}
              </h3>

              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {step?.options?.map((option) => (
                  <button
                    key={option?.id}
                    onClick={() => handleOptionClick(option?.next)}
                    className="w-full bg-muted hover:bg-primary hover:text-primary-foreground border border-border hover:border-primary rounded-xl p-4 md:p-5 text-left transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm md:text-base font-medium">{option?.label}</span>
                      <Icon name="ChevronRight" size={20} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className={`text-center mb-6 md:mb-8 ${step?.urgent ? 'animate-pulse' : ''}`}>
                <h3 className="font-headline font-bold text-xl md:text-2xl mb-4" style={{ color: step?.color }}>
                  {step?.result}
                </h3>
                <p className="font-body text-sm md:text-base text-muted-foreground mb-6">
                  {step?.message}
                </p>
                <Button
                  variant={step?.urgent ? "default" : "outline"}
                  size="lg"
                  onClick={handleEmergencyCall}
                  iconName="Phone"
                  iconPosition="left"
                  className={step?.urgent ? "bg-error hover:bg-error/90 text-error-foreground emergency-cta" : ""}
                >
                  {step?.action}
                </Button>
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-6 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              iconName="ArrowLeft"
              iconPosition="left"
              disabled={history?.length <= 1}
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              {history?.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === history?.length - 1 ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              iconName="RotateCcw"
              iconPosition="right"
            >
              Start Over
            </Button>
          </div>
        </div>

        <div className="mt-8 bg-muted rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Icon name="Info" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h4 className="font-headline font-semibold text-base md:text-lg text-foreground mb-2">
                Important Safety Note
              </h4>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                This diagnostic tool provides general guidance only. If you're unsure about any electrical issue or feel unsafe, always contact a professional electrician immediately. Never attempt electrical repairs without proper training and equipment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaultDiagnosisFlow;